<template>
  <!-- Obra não encontrada -->
  <div v-if="systemNotFound" class="snf-shell">
    <div class="snf-content">
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" opacity=".35"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
      <p>Essa obra não existe.</p>
      <button type="button" @click="goHome">Ir para o início</button>
    </div>
  </div>

  <!-- Login page -->
  <div v-else class="sl-root" :style="rootVars">

    <main class="sl-stage">

      <!-- ── Brand header ── -->
      <header class="sl-brand">
        <div class="sl-brand-logo-wrap">
          <img v-if="systemLogoUrl" :src="$resolveUrl(systemLogoUrl)" alt="logo da obra" class="sl-brand-logo" />
          <span v-else class="sl-brand-name">{{ systemTitle }}</span>
        </div>
        <p class="sl-brand-byline">{{ logoText }}</p>
      </header>

      <!-- ── Suspended ── -->
      <div v-if="isSuspended" class="sl-alert sl-alert--warn">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
        <span>Esta obra está suspensa. Apenas administradores podem acessar.</span>
      </div>

      <!-- ── Offline ── -->
      <div v-else-if="isOffline && isOfflineModeEnabled()" class="sl-alert sl-alert--offline">
        <svg width="16" height="16" viewBox="0 0 20 20" fill="none"><path d="M1 1l18 18M16.72 11.06A10.94 10.94 0 0 1 19 12.55M5 12.55a10.94 10.94 0 0 1 5.17-2.39M10.71 5.05A16 16 0 0 1 22.56 9M1.42 9a15.91 15.91 0 0 1 4.7-2.88M8.53 16.11a6 6 0 0 1 6.95 0M12 20h.01" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
        <div>
          <strong>Sem conexão</strong>
          <span>O login não está disponível offline. Conecte-se à internet para entrar.</span>
        </div>
      </div>

      <!-- ── Form panel ── -->
      <div v-else class="sl-inner">
        <div class="sl-form-head">
          <h2>Entrar</h2>
          <p>Acesse sua conta.</p>
        </div>

        <form class="sl-form" @submit.prevent="handleLogin">
          <div class="sl-field">
            <label for="sl-user">Usuário</label>
            <input id="sl-user" v-model="username" type="text" placeholder="Ex.: joao.silva" autocomplete="username" />
          </div>
          <div class="sl-field">
            <label for="sl-pw">Senha</label>
            <input id="sl-pw" v-model="password" type="password" placeholder="••••••••" autocomplete="current-password" />
          </div>

          <label class="sl-keep">
            <input type="checkbox" v-model="keepLoggedIn" />
            Manter logado
          </label>

          <p v-if="error" class="sl-error">{{ error }}</p>

          <button class="sl-submit" type="submit" :disabled="loading">
            {{ loading ? 'Entrando…' : 'Entrar' }}
          </button>

          <button class="sl-forgot" type="button" @click="forgotPassword">
            Esqueci minha senha
          </button>
        </form>
      </div>

    </main>

    <footer class="sl-footer">
      <span class="sl-pulse" />
      <span>ObraTycoon</span>
    </footer>

  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { accountsStore, type Account } from '@/store/accounts'
import { sessionStore } from '@/store/session'
import { systemStore } from '@/store/system'
import { getSubdomainUsername } from '@/utils/subdomain'
import { isOffline, isOfflineModeEnabled } from '@/composables/useOfflineMode'
import { getAuthToken } from '@/store/api'
import { versionStore } from '@/store/versionStore'

// ── Color utilities ──────────────────────────────────────────────────────
function hexLuminance(hex: string): number {
  const h = hex.startsWith('#') ? hex.slice(1) : hex
  const full = h.length === 3
    ? h.split('').map(c => c + c).join('')
    : h
  if (full.length < 6) return 0.3
  const r = parseInt(full.slice(0, 2), 16) / 255
  const g = parseInt(full.slice(2, 4), 16) / 255
  const b = parseInt(full.slice(4, 6), 16) / 255
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

function darkenHex(hex: string, factor: number): string {
  const h = hex.startsWith('#') ? hex.slice(1) : hex
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h
  if (full.length < 6) return hex
  const r = Math.max(0, Math.round(parseInt(full.slice(0, 2), 16) * (1 - factor)))
  const g = Math.max(0, Math.round(parseInt(full.slice(2, 4), 16) * (1 - factor)))
  const b = Math.max(0, Math.round(parseInt(full.slice(4, 6), 16) * (1 - factor)))
  return '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('')
}

export default defineComponent({
  name: 'SystemLoginView',
  setup() {
    const router = useRouter()
    const route  = useRoute()

    const username      = ref('')
    const password      = ref('')
    const error         = ref('')
    const loading       = ref(false)
    const keepLoggedIn  = ref(false)
    const systemTitle   = ref('Obra')
    const systemLogoUrl = ref('')
    const primaryColor  = ref('#0f4c81')
    const secondaryColor = ref('#e8f0f9')
    const loginBackgroundUrl = ref('')
    const usePrimaryLoginBackground = ref(true)
    const systemNotFound = ref(false)
    const isSuspended    = ref(false)
    const logoText       = ref('by ObraTycoon')

    const systemUsername = computed(() => {
      const sub = getSubdomainUsername()
      if (sub) return sub
      return String(route.params.systemUsername || '').toLowerCase()
    })

    // ── Adaptive color vars ───────────────────────────────────────────────
    const rootVars = computed(() => {
      const primary   = primaryColor.value || '#0f4c81'
      const secondary = secondaryColor.value || '#e8f0f9'
      const hasImage  = !usePrimaryLoginBackground.value && !!loginBackgroundUrl.value

      // Compute relative luminance of primary color
      const lum   = hexLuminance(primary)
      const isDark = lum < 0.45  // most obra colors are dark

      const ink      = isDark ? 'rgba(255,255,255,0.96)' : 'rgba(14,18,28,0.92)'
      const inkSoft  = isDark ? 'rgba(255,255,255,0.66)' : 'rgba(14,18,28,0.6)'
      const inkFaint = isDark ? 'rgba(255,255,255,0.4)'  : 'rgba(14,18,28,0.35)'
      const line     = isDark ? 'rgba(255,255,255,0.28)' : 'rgba(14,18,28,0.2)'
      const grid     = isDark ? 'rgba(255,255,255,0.13)' : 'rgba(14,18,28,0.09)'
      const gridMin  = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(14,18,28,0.04)'
      const bg2      = darkenHex(primary, 0.2)

      // CTA button: secondary color bg + primary text → always good contrast
      const ctaBg = secondary
      const ctaFg = primary

      const bgImage = hasImage
        ? `url("${loginBackgroundUrl.value.startsWith('/uploads/') ? (process.env.VUE_APP_API_BASE || 'http://localhost:3001') + loginBackgroundUrl.value : loginBackgroundUrl.value}")`
        : 'none'

      return {
        '--sl-bg':         primary,
        '--sl-bg-2':       bg2,
        '--sl-ink':        ink,
        '--sl-ink-soft':   inkSoft,
        '--sl-ink-faint':  inkFaint,
        '--sl-line':       line,
        '--sl-grid':       grid,
        '--sl-grid-minor': gridMin,
        '--sl-cta-bg':     ctaBg,
        '--sl-cta-fg':     ctaFg,
        '--sl-accent':     secondary,
        '--sl-bg-image':   bgImage,
      }
    })

    const computeLogoText = (logoType: string, customText: string) => {
      if (logoType === 'custom') return customText
      if (logoType === 'bygusmachado') return 'by GusMachado'
      return 'by ObraTycoon'
    }

    const loadSystemConfig = async () => {
      systemNotFound.value = false
      if (!systemUsername.value) { systemNotFound.value = true; return }
      try {
        const config = await systemStore.refreshPublicByUsername(systemUsername.value)
        systemTitle.value   = config.siteName || 'Obra'
        systemLogoUrl.value = config.siteLogoUrl || ''
        primaryColor.value  = config.colors?.primary || '#0f4c81'
        secondaryColor.value = config.colors?.secondary || '#e8f0f9'
        loginBackgroundUrl.value = config.loginBackgroundUrl || ''
        usePrimaryLoginBackground.value = config.usePrimaryLoginBackground !== false
        isSuspended.value   = Boolean(config.suspended)
        logoText.value      = computeLogoText(config.logoType || 'default', config.customLogoText || '')
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : ''
        systemNotFound.value = message.includes('Obra nao encontrada')
      }
    }

    const handleLogin = async () => {
      error.value = ''
      if (!username.value.trim() || !password.value.trim()) {
        error.value = 'Informe usuário e senha para continuar.'
        return
      }
      loading.value = true
      try {
        const account = await accountsStore.login(
          username.value.trim(),
          password.value.trim(),
          systemUsername.value,
          keepLoggedIn.value
        )
        sessionStore.login({
          name: account.name || account.username,
          role: account.role,
          username: account.username,
          systemId: account.systemId,
          systemUsername: account.systemUsername || systemUsername.value,
          avatar: account.avatar,
          uid: account.uid,
          roleId: account.roleId,
          subordinateUsernames: account.subordinateUsernames,
          hasSubordinates: account.hasSubordinates,
          isAnalyst: Boolean(account.isAnalyst),
          suspended: !!(account as Account & { suspended?: boolean }).suspended
        }, keepLoggedIn.value)
        if ((account as Account & { newVersion?: boolean }).newVersion) {
          versionStore.setNewVersion()
        }
        router.push('/home')
      } catch (err: unknown) {
        error.value = err instanceof Error ? err.message : 'Credenciais inválidas.'
      } finally {
        loading.value = false
      }
    }

    const forgotPassword = () => {
      const sys = systemUsername.value.trim()
      router.push(sys ? `/forgot?system=${encodeURIComponent(sys)}` : '/forgot')
    }

    const goHome = () => router.push('/')

    onMounted(() => {
      if (isOffline.value && isOfflineModeEnabled() && getAuthToken()) {
        router.push('/home')
        return
      }
      loadSystemConfig()
      if (sessionStore.isLoggedIn.value && sessionStore.state.user) {
        router.push('/home')
      }
    })
    watch(systemUsername, () => loadSystemConfig())

    return {
      username, password, error, loading, keepLoggedIn,
      systemTitle, systemLogoUrl, systemNotFound, isSuspended, logoText,
      rootVars,
      handleLogin, forgotPassword, goHome,
      isOffline, isOfflineModeEnabled
    }
  }
})
</script>

<style scoped lang="scss">
// ── "Obra not found" screen ───────────────────────────────────────────────
.snf-shell {
  min-height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #0d1826;
  color: rgba(255,255,255,0.8);
  font-family: "Outfit", system-ui, sans-serif;
}

.snf-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  text-align: center;

  p { font-size: 15px; margin: 0; }

  button {
    background: transparent;
    border: 1px solid rgba(255,255,255,0.3);
    color: rgba(255,255,255,0.8);
    border-radius: 4px;
    padding: 0.65em 1.4em;
    font-size: 14px;
    cursor: pointer;
    font-family: inherit;
    transition: background 0.2s, border-color 0.2s;

    &:hover { background: rgba(255,255,255,0.08); border-color: rgba(255,255,255,0.5); }
  }
}

// ── Root ─────────────────────────────────────────────────────────────────
.sl-root {
  min-height: 100svh;
  min-height: 100dvh;
  display: grid;
  place-items: center;
  position: relative;
  background: var(--sl-bg, #0f4c81);
  background-image: var(--sl-bg-image, none);
  background-size: cover;
  background-position: center;
  color: var(--sl-ink);
  font-family: "Outfit", system-ui, sans-serif;
  -webkit-font-smoothing: antialiased;
  overflow: hidden;

  // Blueprint grid
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(to right, var(--sl-grid) 1px, transparent 1px),
      linear-gradient(to bottom, var(--sl-grid) 1px, transparent 1px),
      linear-gradient(to right, var(--sl-grid-minor) 1px, transparent 1px),
      linear-gradient(to bottom, var(--sl-grid-minor) 1px, transparent 1px);
    background-size: 96px 96px, 96px 96px, 24px 24px, 24px 24px;
    background-position: center;
    -webkit-mask-image: radial-gradient(ellipse 90% 86% at 50% 50%, #000 58%, transparent 100%);
    mask-image: radial-gradient(ellipse 90% 86% at 50% 50%, #000 58%, transparent 100%);
    pointer-events: none;
    z-index: 0;
  }

  // Vignette (darker edges)
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(ellipse 72% 64% at 50% 48%, transparent 42%, var(--sl-bg-2, #0a3460) 100%);
    pointer-events: none;
    z-index: 0;
  }

  // When using a background image: dark overlay for readability
  &:has([style*="--sl-bg-image"]>*) {
    &::before { opacity: 0.5; }
  }
}

// ── Stage ─────────────────────────────────────────────────────────────────
.sl-stage {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: clamp(20px, 3.5vh, 36px);
  padding: clamp(24px, 5vw, 64px) clamp(20px, 4vw, 40px);
  width: 100%;
  max-width: 440px;
  text-align: center;
}

// ── Brand ─────────────────────────────────────────────────────────────────
.sl-brand {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  animation: sl-fade-up 0.8s ease 0.1s both;
}

.sl-brand-logo-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
}

.sl-brand-logo {
  max-width: 220px;
  max-height: 60px;
  object-fit: contain;
}

.sl-brand-name {
  font-size: clamp(28px, 6vw, 46px);
  font-weight: 600;
  letter-spacing: -0.03em;
  color: var(--sl-ink);
  line-height: 1;
}

.sl-brand-byline {
  font-size: 13px;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: var(--sl-ink-faint);
  margin: 0;
  font-weight: 400;
}

// ── Alerts ────────────────────────────────────────────────────────────────
.sl-alert {
  width: 100%;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  border-radius: 6px;
  padding: 12px 14px;
  font-size: 13px;
  text-align: left;
  animation: sl-fade-up 0.5s ease both;

  svg { flex-shrink: 0; margin-top: 1px; }

  &--warn {
    background: rgba(251,191,36,0.15);
    border: 1px solid rgba(251,191,36,0.4);
    color: var(--sl-ink);

    svg { color: #fbbf24; }
  }

  &--offline {
    background: rgba(255,255,255,0.08);
    border: 1px solid var(--sl-line);
    color: var(--sl-ink-soft);

    strong { display: block; font-weight: 600; color: var(--sl-ink); margin-bottom: 3px; }
    span   { display: block; line-height: 1.5; }
    svg { color: var(--sl-ink-soft); margin-top: 3px; }
  }
}

// ── Inner panel ───────────────────────────────────────────────────────────
.sl-inner {
  width: 100%;
  animation: sl-fade-up 0.8s ease 0.25s both;
}

.sl-form-head {
  margin-bottom: clamp(18px, 3vh, 26px);
  text-align: left;

  h2 {
    font-size: clamp(26px, 4.5vw, 38px);
    font-weight: 600;
    letter-spacing: -0.02em;
    color: var(--sl-ink);
    margin: 0 0 4px;
    line-height: 1.05;
  }

  p {
    margin: 0;
    font-size: 15px;
    color: var(--sl-ink-soft);
  }
}

// ── Form ──────────────────────────────────────────────────────────────────
.sl-form {
  display: flex;
  flex-direction: column;
  gap: clamp(12px, 2vh, 16px);
}

.sl-field {
  display: flex;
  flex-direction: column;
  gap: 7px;
  text-align: left;

  label {
    font-size: 11px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--sl-ink-soft);
  }

  input {
    font-family: inherit;
    font-size: 15px;
    color: var(--sl-ink);
    background: rgba(255,255,255,0.06);
    border: 1px solid var(--sl-line);
    border-radius: 4px;
    padding: 0.8em 0.9em;
    outline: none;
    transition: border-color 0.2s ease, background 0.2s ease;

    &::placeholder { color: var(--sl-ink-faint); }

    &:focus {
      border-color: var(--sl-accent, rgba(255,255,255,0.6));
      background: rgba(255,255,255,0.1);
    }
  }
}

.sl-keep {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--sl-ink-soft);
  cursor: pointer;
  user-select: none;
  text-align: left;

  input[type='checkbox'] {
    width: 15px;
    height: 15px;
    cursor: pointer;
    accent-color: var(--sl-accent, #fff);
    flex-shrink: 0;
  }
}

.sl-error {
  font-size: 13px;
  margin: 0;
  padding: 9px 12px;
  border-radius: 4px;
  border: 1px solid rgba(239,68,68,0.4);
  background: rgba(239,68,68,0.12);
  color: oklch(0.78 0.18 25);
  text-align: left;
  line-height: 1.4;
}

// ── Submit button ─────────────────────────────────────────────────────────
.sl-submit {
  font-family: inherit;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.03em;
  color: var(--sl-cta-fg);
  background: var(--sl-cta-bg);
  border: none;
  border-radius: 4px;
  padding: 0.92em;
  margin-top: 4px;
  cursor: pointer;
  width: 100%;
  transition: transform 0.2s ease, filter 0.2s ease, opacity 0.15s;

  &:hover:not(:disabled) { transform: translateY(-1px); filter: brightness(1.04); }
  &:active:not(:disabled) { transform: translateY(0); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
}

// ── Forgot link ───────────────────────────────────────────────────────────
.sl-forgot {
  background: none;
  border: none;
  padding: 0;
  font-family: inherit;
  font-size: 13.5px;
  color: var(--sl-ink-soft);
  cursor: pointer;
  text-align: center;
  border-bottom: 1px solid transparent;
  transition: color 0.2s ease, border-color 0.2s ease;
  align-self: center;

  &:hover {
    color: var(--sl-ink);
    border-bottom-color: var(--sl-ink-soft);
  }
}

// ── Footer ────────────────────────────────────────────────────────────────
.sl-footer {
  position: fixed;
  bottom: clamp(16px, 3vh, 28px);
  left: 0;
  right: 0;
  z-index: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  font-weight: 400;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--sl-ink-faint);
  pointer-events: none;
  animation: sl-fade-up 0.8s ease 0.6s both;
}

.sl-pulse {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--sl-accent, rgba(255,255,255,0.5));
  flex-shrink: 0;
  animation: sl-pulse 2.6s ease-out infinite;
}

// ── Animations ────────────────────────────────────────────────────────────
@keyframes sl-fade-up {
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
}

@keyframes sl-pulse {
  0%   { box-shadow: 0 0 0 0 var(--sl-accent, rgba(255,255,255,0.4)); }
  70%  { box-shadow: 0 0 0 8px transparent; }
  100% { box-shadow: 0 0 0 0 transparent; }
}

@media (prefers-reduced-motion: reduce) {
  .sl-brand, .sl-inner, .sl-footer { animation: none; opacity: 1; transform: none; }
}

// ── Responsive ────────────────────────────────────────────────────────────
@media (max-width: 480px) {
  .sl-stage {
    padding-bottom: max(80px, env(safe-area-inset-bottom, 80px));
  }
}
</style>
